/**
 * Property Documents Utility
 * 
 * Maps document types to display names and generates document list for property details
 */

import { DOCUMENT_SF_NAMES, DOCUMENT_TYPES, PropertyDocument, type DocumentSFName, type DocumentType } from '@/modules/profile/constants/documentTypes';

/**
 * Document type configuration with display information
 */
export interface PropertyDocumentConfig {
    docType: DocumentType;
    translationKey: string; // Translation key for document name
    order: number; // Display order in the list
    name: DocumentSFName; // Name to match with document list from API
}

/**
 * List of property documents from Figma design
 * 
 * NOTE: As per latest requirements, **Account statement** and **Receipt history**
 * should NOT be shown in the property details documents list (on both web and app).
 * Therefore we only expose:
 * - Title deed
 * - SPA document
 */
export const PROPERTY_DOCUMENTS: readonly PropertyDocumentConfig[] = [
    { docType: DOCUMENT_TYPES.TITLE_DEED, translationKey: 'propertyDetails.documentsListTitleDeed', order: 1, name: DOCUMENT_SF_NAMES.TITLE_DEED },
    { docType: DOCUMENT_TYPES.SPA, translationKey: 'propertyDetails.documentsListSpa', order: 2, name: DOCUMENT_SF_NAMES.SPA },
    // { docType: DOCUMENT_TYPES.FLOOR_PLAN, translationKey: 'propertyDetails.documentsListFloorPlan', order: 3 , name: DOCUMENT_SF_NAMES.FLOOR_PLAN },
    // { docType: DOCUMENT_TYPES.PASSPORT, translationKey: 'propertyDetails.documentsListPassport', order: 4 , name: DOCUMENT_SF_NAMES.PASSPORT },
    // { docType: DOCUMENT_TYPES.EID_FRONT, translationKey: 'propertyDetails.documentsListEidFront', order: 5 , name: DOCUMENT_SF_NAMES.EID_FRONT },
    // { docType: DOCUMENT_TYPES.EID_BACK, translationKey: 'propertyDetails.documentsListEidBack', order: 6 , name: DOCUMENT_SF_NAMES.EID_BACK },
    // Intentionally omit DOCUMENT_TYPES.SALES_SOA (Account statement)
    // and DOCUMENT_TYPES.SALES_RECEIPT (Receipt history) from this list
] as const;

/**
 * Get all property documents sorted by display order
 */
export function getPropertyDocuments(documents: PropertyDocument): readonly PropertyDocumentConfig[] {
    let availableDocs : PropertyDocumentConfig[] = [];
    documents.forEach(doc => {
        const docDetails = getDocumentDetails(doc.name as DocumentSFName);
        if(docDetails) availableDocs.push(docDetails);
    });

    return [...availableDocs].sort((a, b) => a.order - b.order);
}

export function checkFloorPlanAvailability(documents: PropertyDocument): boolean {
    return documents.some(doc => doc.name === DOCUMENT_SF_NAMES.FLOOR_PLAN);
}

/**
 * Find document config by document type
 */
export function getDocumentDetails(docName: DocumentSFName): PropertyDocumentConfig | undefined {
    return PROPERTY_DOCUMENTS.find(doc => doc?.name === docName);
}

